/* oxlint-disable no-restricted-syntax */
export const isTest = process.env.TEST === 'true';
export const isServer = typeof window === 'undefined';

export const isBuild = process.env.NODE_ENV === 'production';
export const isVercelLive = !isServer && typeof window.__vercel_toolbar !== 'undefined';

declare global {
  interface Window {
    __vercel_toolbar?: {
      isAuthenticated: boolean;
      unmount: () => void;
    };
  }
}

/**
 * 아래와 같이 처리됩니다.
 *
 * - `app-[version].[environment].prex.run` => `[environment].prex.run`
 * - `[version].app-[environment].prex.run` => `[environment].prex.run`
 * - `app.[prod-domain]` => `[prod-domain]`
 * - `app-[version].[prod-domain]` => `[prod-domain]`
 * - `[version].app.[prod-domain]` => `[prod-domain]`
 *
 * 각 변수들이 의미는 다음과 같습니다.
 *
 * - `[environment]`: `sandbox`, `stg`, `prod`
 * - `[version]`: `v1-2-3`, ...
 * - `[prod-domain]`: `flipster.io`, `flipster.me`
 */
export const removeWebviewPrefix = (hostname: string) =>
  hostname.replace(/^app(?:-v(?:\w|-)+)?\.|^v(?:\w|-)+\.app(?:\.|-)|^app-/, '');

const isWebviewHost = (host: string): boolean => {
  if (host.startsWith('localhost')) return host.endsWith(':3001');
  // starts with "app." or "app-"
  // or includes ".app." or ".app-"
  return !!host.match(/((^|.)app[.-])/);
};

export const convertWebviewToWebUrl = (path: string, fn?: (webview: boolean) => URLSearchParams): string => {
  const { protocol, hostname, host } = window.location;
  const origin = hostname === 'localhost' ? 'https://stg.prex.run' : `${protocol}//${removeWebviewPrefix(hostname)}`;
  const url = new URL(path, origin);
  if (fn) {
    const params = fn(isWebviewHost(host));
    for (const [name, value] of params.entries()) {
      url.searchParams.append(name, value);
    }
  }
  return url.href;
};

export const gotoFlipster = (path: string, fn?: (webview: boolean) => URLSearchParams) => {
  window.location.href = convertWebviewToWebUrl(path, fn);
};

export const getServerHostname = (): string => {
  const serverUrl = process.env.NEXT_PUBLIC_SERVER_URL ?? '';
  // SSR 시에는 언제나 주입받은 환경변수를 사용함
  if (isServer) return serverUrl;
  const { protocol, host, hostname } = window.location;
  const isLocalhost = isLocal();
  // 로컬 개발환경에서는 next.config.js 혹은 proxy-server.js에서
  // API 서버를 리버스 프록시하고 있으므로 window.location.host를 그대로 사용함
  if (isLocalhost) return `${protocol}//${host}`;

  // NEXT_PUBLIC_SERVER_URL을 주입받았다면 SSR 하는 경우와 다른 서버를 바라보면 안되므로 주입받은 주소를 사용함
  // window.location.hostname이 .yoda.mobi로 끝나는 경우는 젠킨스로 빌드되는 경우임. 아래 분기에서 별도 처리
  if (serverUrl && !hostname.endsWith('.yoda.mobi')) return serverUrl;

  // 젠킨스로 빌드되는 경우에는 아래 이유로 window.location.hostname으로부터 API 서버 주소를 유추함:
  // - 젠킨스로 빌드되는 웹 프로젝트의 경우에는 ns에서 웹 이미지를 어떻게 지정했는지에 따라 주입받은 NEXT_PUBLIC_SERVER_URL이 엉뚱한 값일 수 있음
  //   - 예: NEXT_PUBLIC_SERVER_URL에 luke.yoda.mobi가 지정된 이미지를 다른 ns에서 사용하게 되는 경우
  // - 젠킨스로 빌드되는 웹뷰 프로젝트의 경우에는 NEXT_PUBLIC_SERVER_URL을 주입받지 않고 있음

  // 타팀에서 웹뷰 테스트가 필요한 경우 사용하는 주소
  // *.yoda.mobi => api-*.yoda.mobi
  // app-*.yoda.mobi => api-*.yoda.mobi
  // qa-test-*.yoda.mobi => api-luke.yoda.mobi
  if (hostname.endsWith('.yoda.mobi')) {
    if (hostname.startsWith('qa-test-')) return `${protocol}//api-luke.yoda.mobi`;
    return `${protocol}//api-${hostname.replace(/^app-/, '')}`;
  }

  return `${protocol}//api.${removeWebviewPrefix(hostname)}`;
};

export const isLocal = () => {
  if (isServer) return false;
  const { hostname } = window.location;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

export const getServerEnv = () => {
  const hostname = getServerHostname();

  if (hostname.includes('sandbox')) return 'sandbox';
  if (hostname.includes('stg')) return 'stg';
  if (hostname.includes('prod.') || hostname.includes('flipster')) return 'prod';
  return 'dev';
};

export const isProd = getServerEnv() === 'prod';
